import { useEffect, useState } from "react";
import {
  Container,
  Box,
  Typography,
  Grid,
  TextField,
  Checkbox,
  FormControlLabel,
  Alert,
  CircularProgress,
  Button,
} from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import { apiVariable } from "../../../api/apiVariable";

type Draft = {
  email: string;
  firstName: string;
  lastName: string;
  useFoxCGroupInc: boolean;
  useC1TransportationInc: boolean;

  escrow: boolean;
  escrowAmount: number;
  eldDevice: boolean;
  eldDeviceAmount: number;
  prepass: boolean;
  prepassAmount: number;
  cargoInsurance: boolean;
  cargoInsuranceAmount: number;
  occupationalInsurance: boolean;
  occupationalInsuranceAmount: number;
  parkingSpot: boolean;
  parkingSpotAmount: number;
  driversLegalPlan: boolean;
  driversLegalPlanAmount: number;
  eldDeviceSecurityDeposit: boolean;
  eldDeviceSecurityDepositAmount: number;
  irp: boolean;
  irpAmount: number;
  eldMonitoring: boolean;
  eldMonitoringAmount: number;

  truckDownPayment: number;
  truckLease: number;
  trailerRent: number;
  truckPhysicalInsurance: number;
  trailerPhysicalInsurance: number;
  payRatePercent: number;
};

export default function DeductionAuthorizationReviewPage() {
  const { token } = useParams<{ token: string }>();
  const nav = useNavigate();
  const [draft, setDraft] = useState<Draft | null>(null);
  const [sigDate, setSigDate] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [msg, setMsg] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${apiVariable}/${token}`);
        if (!r.ok) throw new Error("Link expired or not found");
        setDraft(await r.json());
      } catch (e: any) {
        setMsg(e.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const sign = async () => {
    if (!sigDate) return setMsg("Pick a signature date first.");
    setSending(true);
    setMsg("");
    try {
      await fetch(`${apiVariable}/${token}/sign`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(sigDate),
      });
      setMsg("Signed! A PDF copy will arrive by e-mail.");
      setTimeout(() => nav("/thank-you"), 2500);
    } catch {
      setMsg("Network error – try again.");
    } finally {
      setSending(false);
    }
  };

  if (loading) return <CircularProgress sx={{ mt: 8, ml: 8 }} />;
  if (!draft)
    return (
      <Alert severity="error" sx={{ mt: 4 }}>
        {msg}
      </Alert>
    );

  const carrier = draft.useFoxCGroupInc
    ? "Fox C Group Inc."
    : draft.useC1TransportationInc
      ? "C1 Transportation Inc."
      : "(No Carrier Selected)";

  const ro = { disabled: true, InputProps: { readOnly: true } };
  const fmt = (v: number | null | undefined) =>
    typeof v === "number" ? v.toFixed(2) : "0.00";

  const Money = (label: string, val: number | null | undefined) => (
    <Grid item xs={12} sm={6}>
      <TextField fullWidth label={label} value={fmt(val)} {...ro} />
    </Grid>
  );

  const Flag = (
    flag: boolean,
    amt: number | null | undefined,
    label: string,
  ) => (
    <Grid container spacing={1} alignItems="center" key={label}>
      <Grid item xs={12} sm={8}>
        <FormControlLabel
          control={<Checkbox checked={flag} disabled />}
          label={label}
        />
      </Grid>
      <Grid item xs={12} sm={4}>
        <TextField size="small" value={fmt(amt)} {...ro} />
      </Grid>
    </Grid>
  );

  return (
    <Container maxWidth="md">
      <Box
        sx={{
          mt: 4,
          p: { xs: 2, sm: 3 },
          boxShadow: 3,
          borderRadius: 2,
          bgcolor: "#fff",
        }}
      >
        <Typography variant="h4" align="center" gutterBottom>
          Review & Sign Deduction Authorization
        </Typography>
        {msg && (
          <Alert sx={{ mb: 2 }} severity="info">
            {msg}
          </Alert>
        )}

        <Typography paragraph>
          I,{" "}
          <b>
            {draft.firstName} {draft.lastName}
          </b>
          , understand and agree that Carrier, <b>{carrier}</b>, may deduct
          money from my statement for the following items.
        </Typography>
        <Typography paragraph>
          I also understand and agree that my pay rate will be{" "}
          <b>{draft.payRatePercent}%</b>.
        </Typography>
        <Typography paragraph>
          By providing my typed first and last name below, I authorize it to be
          used as my digital signature.
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField fullWidth label="Email" value={draft.email} {...ro} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="First name"
              value={draft.firstName}
              {...ro}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Last name"
              value={draft.lastName}
              {...ro}
            />
          </Grid>

          <Grid item xs={12}>
            <FormControlLabel
              control={<Checkbox checked={draft.useFoxCGroupInc} disabled />}
              label="Fox C Group Inc."
            />
            <FormControlLabel
              control={
                <Checkbox checked={draft.useC1TransportationInc} disabled />
              }
              label="C1 Transportation Inc."
            />
          </Grid>

          <Grid item xs={12}>
            <Typography variant="h6">Deductions selected</Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            {Flag(draft.escrow, draft.escrowAmount, "Escrow")}
            {Flag(draft.eldDevice, draft.eldDeviceAmount, "ELD Device / month")}
            {Flag(draft.prepass, draft.prepassAmount, "Prepass / month")}
            {Flag(
              draft.cargoInsurance,
              draft.cargoInsuranceAmount,
              "Cargo Insurance / week",
            )}
            {Flag(
              draft.occupationalInsurance,
              draft.occupationalInsuranceAmount,
              "Occupational Insurance / month",
            )}
          </Grid>
          <Grid item xs={12} sm={6}>
            {Flag(
              draft.parkingSpot,
              draft.parkingSpotAmount,
              "Parking Spot / month",
            )}
            {Flag(
              draft.driversLegalPlan,
              draft.driversLegalPlanAmount,
              "Driver's Legal Plan / month",
            )}
            {Flag(
              draft.eldDeviceSecurityDeposit,
              draft.eldDeviceSecurityDepositAmount,
              "ELD Device Security Deposit",
            )}
            {Flag(draft.irp, draft.irpAmount, "IRP / week")}
            {Flag(
              draft.eldMonitoring,
              draft.eldMonitoringAmount,
              "ELD Monitoring",
            )}
          </Grid>

          <Grid item xs={12}>
            <Typography variant="h6">Money-based deductions</Typography>
          </Grid>
          {Money("Truck Down Payment", draft.truckDownPayment)}
          {Money("Truck Lease (weekly)", draft.truckLease)}
          {Money("Trailer Rent (monthly)", draft.trailerRent)}
          {Money(
            "Truck Physical Insurance (monthly)",
            draft.truckPhysicalInsurance,
          )}
          {Money(
            "Trailer Physical Insurance (monthly)",
            draft.trailerPhysicalInsurance,
          )}
          {Money("Pay Rate (%)", draft.payRatePercent)}

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type="date"
              label="Signature date"
              value={sigDate}
              onChange={(e) => setSigDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12}>
            <Button
              fullWidth
              variant="contained"
              onClick={sign}
              disabled={sending}
              startIcon={sending && <CircularProgress size={20} />}
            >
              {sending ? "Submitting…" : "Sign & submit"}
            </Button>
          </Grid>
        </Grid>
      </Box>
    </Container>
  );
}
